import { useState, useEffect } from 'react';

export const STORAGE_WARNING_THRESHOLDS = {
  WARNING: 0.7, // 70%
  CRITICAL: 0.9, // 90%
} as const;

export const isOnline = (): boolean => navigator.onLine;

export const useOnlineStatus = () => {
  const [online, setOnline] = useState(isOnline());

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
};

export interface StorageEstimate {
  quota?: number;
  usage?: number;
  usageDetails?: {
    indexedDB?: number;
    caches?: number;
    localStorage?: number;
  };
}

export const getStorageEstimate = async (): Promise<StorageEstimate | null> => {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      return await navigator.storage.estimate();
    } catch (error) {
      console.error('Error getting storage estimate:', error);
      return null;
    }
  }
  return null;
};

export const getStorageUsagePercentage = (estimate: StorageEstimate): number => {
  if (estimate.quota && estimate.usage) {
    return estimate.usage / estimate.quota;
  }
  return 0;
};

export const getStorageWarningLevel = (percentage: number): 'none' | 'warning' | 'critical' => {
  if (percentage >= STORAGE_WARNING_THRESHOLDS.CRITICAL) return 'critical';
  if (percentage >= STORAGE_WARNING_THRESHOLDS.WARNING) return 'warning';
  return 'none';
};

export const formatStorageSize = (bytes: number): string => {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

export const useStorageMonitor = () => {
  const [estimate, setEstimate] = useState<StorageEstimate | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshEstimate = async () => {
    setLoading(true);
    const newEstimate = await getStorageEstimate();
    setEstimate(newEstimate);
    setLoading(false);
  };

  useEffect(() => {
    refreshEstimate();
  }, []);

  const percentage = estimate ? getStorageUsagePercentage(estimate) : 0;
  const warningLevel = getStorageWarningLevel(percentage);

  return {
    estimate,
    percentage,
    warningLevel,
    loading,
    refreshEstimate,
  };
};

export const useReadonlyMode = () => !useOnlineStatus();
